import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Table, Typography } from 'antd';
import moment from 'moment';

import CompanySettingsCard from '../CompanySettingsCard';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import ConfirmPasswordModal from '../ConfirmPasswordModal';
import SettingsService from '../state/settings.service';
import EmailBlockListModal from './EmailBlockListModal';
import {
  HEADER_CELL_STYLE,
  SCOPE_LABELS,
  blockTitle,
} from './emailBlockListHelpers';
import { safeExtractFromObject } from '../../helpers/helpers';

const { Text } = Typography;

const PREVIEW_ROW_LIMIT = 5;

function EmailBlockList() {
  const [blocks, setBlocks] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [unblockId, setUnblockId] = useState(null);

  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const hasRedesign = settings?.uxRedesignPt1;

  const loadBlocks = useCallback(async () => {
    const result = await SettingsService.getEmailSendBlocks();
    if (result?.err) {
      console.error(result?.err);
      setBlocks([]);
      return;
    }
    setBlocks(result?.data?.blocks || []);
  }, []);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  useEffect(() => {
    if (isModalVisible && blocks.length === 0) {
      setIsModalVisible(false);
    }
  }, [blocks.length, isModalVisible]);

  const openModal = () => {
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  const closePasswordConfirmModal = () => {
    setUnblockId(null);
  };

  const handleUnblockClick = (blockId) => {
    setUnblockId(blockId);
  };

  const handlePasswordModalConfirm = async (password) => {
    const result = await SettingsService.unblockEmailSending({
      blockId: unblockId,
      password,
    });
    if (result?.err) return false;
    setUnblockId(null);
    await loadBlocks();
    return true;
  };

  const onHeaderRow = useCallback(() => {
    if (!hasRedesign) return null;
    return {
      className: 'ontraccr-table-header',
    };
  }, [hasRedesign]);

  const previewColumns = useMemo(() => [
    {
      title: 'Block',
      key: 'block',
      onHeaderCell: () => ({ style: HEADER_CELL_STYLE }),
      render: (_, block) => <Text strong>{blockTitle(block)}</Text>,
    },
    {
      title: 'Scope',
      key: 'scope',
      width: 96,
      onHeaderCell: () => ({ style: HEADER_CELL_STYLE }),
      render: (_, block) => SCOPE_LABELS[block.scope] || block.scope,
    },
    {
      title: 'Blocked on',
      key: 'createdAt',
      width: 112,
      onHeaderCell: () => ({ style: HEADER_CELL_STYLE }),
      render: (_, block) => (
        block.createdAt
          ? moment(block.createdAt).format('MMM D, YYYY')
          : <Text type="secondary">-</Text>
      ),
    },
  ], []);

  const blockCount = blocks.length;
  const previewBlocks = blocks.slice(0, PREVIEW_ROW_LIMIT);
  const hasMoreBlocks = blockCount > PREVIEW_ROW_LIMIT;

  return (
    <CompanySettingsCard
      title="Email Block List"
      leftHeaderView={blockCount > 0 ? (
        <OnTraccrButton title="View" onClick={openModal} />
      ) : null}
    >
      <ConfirmPasswordModal
        visible={unblockId != null}
        onClose={closePasswordConfirmModal}
        onConfirm={handlePasswordModalConfirm}
      />
      <EmailBlockListModal
        visible={isModalVisible}
        onClose={closeModal}
        blocks={blocks}
        onUnblock={handleUnblockClick}
      />
      {blockCount === 0 ? (
        <Text type="secondary" style={{ display: 'block', padding: '16px 0' }}>
          No emails are blocked from sending.
        </Text>
      ) : (
        <>
          <Table
            columns={previewColumns}
            dataSource={previewBlocks}
            rowKey="id"
            size="small"
            pagination={false}
            onHeaderRow={onHeaderRow}
            style={{
              width: '100%',
              marginBottom: hasMoreBlocks ? 8 : 0,
            }}
          />
          {hasMoreBlocks && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              {blockCount - PREVIEW_ROW_LIMIT}
              {' '}
              more — use View for the full list.
            </Text>
          )}
        </>
      )}
    </CompanySettingsCard>
  );
}

EmailBlockList.propTypes = {};

export default EmailBlockList;
